import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import JSZip from "jszip";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { 
  ArrowLeft, Upload, FileText, FileSpreadsheet, Trash2, 
  Eye, Calendar, Euro, User, Clock, Folder, FolderPlus,
  AlertTriangle, Loader2, MoreVertical, FolderOpen, MoveRight,
  ChevronRight, ChevronDown, ChevronUp, Pencil, X, Download, Maximize2, Paperclip,
  Archive, Image
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import ContractFormModal from "../components/ContractFormModal";
import PDFViewerModal from "../components/PDFViewerModal";
import { ResiliationClientCard, ResiliationClientUploadContracts } from "../components/engagements/ResiliationClientContracts";

import ContractHighlightFields from "../components/contracts/ContractHighlightFields";
import ClientContractRenewalSection from "../components/ClientContractRenewalSection";
import { RotateCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const formatDateFR = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) {
    return <FileText className="w-6 h-6 text-red-500" />;
  } else if (contentType?.includes('spreadsheet') || contentType?.includes('excel')) {
    return <FileSpreadsheet className="w-6 h-6 text-green-600" />;
  } else if (contentType?.includes('word') || contentType?.includes('document')) {
    return <FileText className="w-6 h-6 text-blue-600" />;
  } else if (contentType?.includes('image')) {
    return <Image className="w-6 h-6 text-purple-500" />;
  }
  return <FileText className="w-6 h-6 text-gray-500" />;
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-100 text-green-700 border-green-300">Actif</Badge>;
    case 'expiring_soon':
      return <Badge className="bg-orange-100 text-orange-700 border-orange-300">Expire bientôt</Badge>;
    case 'expired':
      return <Badge className="bg-red-100 text-red-700 border-red-300">Expiré</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export default function ContratDetail() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [contract, setContract] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Folder states
  const [expandedFolders, setExpandedFolders] = useState({});
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // For upload target
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [documentToMove, setDocumentToMove] = useState(null);
  const [renameFolderDialog, setRenameFolderDialog] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  
  // Multi-selection states
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = useState(false);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  
  // Document viewer states
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentBlob, setDocumentBlob] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [wordHtml, setWordHtml] = useState(null);
  
  // Edit contract modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showInlineTacit, setShowInlineTacit] = useState(false);
  
  // Related invoices
  const [relatedInvoices, setRelatedInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  
  // Préavis éditable
  const [editingPreavis, setEditingPreavis] = useState(false);
  const [preavisValue, setPreavisValue] = useState(3);
  const [savingPreavis, setSavingPreavis] = useState(false);

  useEffect(() => {
    fetchContractData();
  }, [contractId, token]);
  
  // Mettre à jour preavisValue quand le contrat est chargé
  useEffect(() => {
    if (contract) {
      setPreavisValue(contract.duree_preavis_resiliation_mois || 3);
    }
  }, [contract]);

  // Fetch related invoices when contract is loaded
  useEffect(() => {
    if (contract?.societe) {
      fetchRelatedInvoices(contract.societe);
    }
  }, [contract?.societe, token]);

  const fetchRelatedInvoices = async (clientName) => {
    if (!token || !clientName) return;
    setLoadingInvoices(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/contracts/link/${encodeURIComponent(clientName)}`, { headers });
      setRelatedInvoices(res.data.invoices?.items || []);
    } catch (error) {
      console.error("Error fetching related invoices:", error);
    } finally {
      setLoadingInvoices(false);
    }
  };
  
  const handleSavePreavis = async () => {
    if (!token || !contract) return;
    setSavingPreavis(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/contracts/${contractId}/preavis`,
        { duree_preavis_mois: preavisValue },
        { headers }
      );
      
      // Rafraîchir les données du contrat
      await fetchContractData();
      setEditingPreavis(false);
    } catch (error) {
      console.error("Error saving preavis:", error);
      alert("Erreur lors de la sauvegarde du préavis");
    } finally {
      setSavingPreavis(false);
    }
  };

  const fetchContractData = async () => {
    if (!token || !contractId) return;
    setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch contract details
      const contractRes = await axios.get(`${API}/contracts/${contractId}`, { headers });
      setContract(contractRes.data);
      
      // Fetch documents and folders
      const docsRes = await axios.get(`${API}/contracts/${contractId}/documents`, { headers });
      setDocuments(docsRes.data.documents || []);
      setFolders(docsRes.data.folders || []);
      
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setCreatingFolder(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/contracts/${contractId}/folders?name=${encodeURIComponent(newFolderName.trim())}`,
        {},
        { headers }
      );
      
      await fetchContractData();
      setNewFolderDialogOpen(false);
      setNewFolderName("");
      
    } catch (error) {
      console.error("Error creating folder:", error);
      alert("Erreur lors de la création du dossier");
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRenameFolder = async () => {
    if (!renameFolderName.trim() || !renameFolderDialog) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/contracts/${contractId}/folders/${renameFolderDialog.id}?name=${encodeURIComponent(renameFolderName.trim())}`,
        {},
        { headers }
      );
      
      await fetchContractData();
      setRenameFolderDialog(null);
      setRenameFolderName("");
      
    } catch (error) {
      console.error("Error renaming folder:", error);
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Supprimer le dossier "${folderName}" ? Les documents seront déplacés à la racine.`)) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/contracts/${contractId}/folders/${folderId}`, { headers });
      await fetchContractData();
    } catch (error) {
      console.error("Error deleting folder:", error);
    }
  };

  const handleFileUpload = async (e, folderId = null) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        let url = `${API}/contracts/${contractId}/documents`;
        if (folderId) {
          url += `?folder_id=${folderId}`;
        }
        
        await axios.post(url, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      await fetchContractData();
      
    } catch (error) {
      console.error("Error uploading file:", error);
      alert(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      setSelectedFolderId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleMoveDocument = async (targetFolderId) => {
    if (!documentToMove) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = `${API}/contracts/${contractId}/documents/${documentToMove.id}/move`;
      if (targetFolderId) {
        url += `?folder_id=${targetFolderId}`;
      }
      
      await axios.put(url, {}, { headers });
      await fetchContractData();
      setMoveDialogOpen(false);
      setDocumentToMove(null);
      
    } catch (error) {
      console.error("Error moving document:", error);
    }
  };

  const handleViewDocument = async (doc) => {
    setViewingDocument(doc);
    setViewerOpen(true);
    setLoadingDocument(true);
    setDocumentBlob(null);
    setExcelData(null);
    setWordHtml(null);
    
    try {
      // Use fetch instead of axios to avoid arraybuffer parsing issues on error
      const response = await fetch(
        `${API}/contracts/${contractId}/documents/${doc.id}`,
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Check for error status codes
      if (response.status === 404) {
        alert("Document non trouvé. Le fichier a peut-être été supprimé ou n'a jamais été téléchargé correctement.");
        setViewerOpen(false);
        setLoadingDocument(false);
        return;
      }
      
      if (!response.ok) {
        alert("Erreur lors du chargement du document");
        setViewerOpen(false);
        setLoadingDocument(false);
        return;
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const contentType = doc.content_type || '';
      
      // Handle Excel files
      if (contentType.includes('spreadsheet') || contentType.includes('excel') || 
          doc.filename.endsWith('.xlsx') || doc.filename.endsWith('.xls')) {
        try {
          // Use cellDates: true to parse dates properly, and raw: false to get formatted values
          const workbook = XLSX.read(arrayBuffer, { 
            type: 'array',
            cellDates: true,
            cellNF: true,
            cellStyles: true
          });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Use sheet_to_json with raw:false to get formatted strings
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1,
            raw: false,
            dateNF: 'dd/mm/yyyy'
          });
          
          setExcelData({
            sheetNames: workbook.SheetNames,
            currentSheet: firstSheetName,
            data: jsonData,
            workbook: workbook
          });
        } catch (e) {
          console.error("Error parsing Excel:", e);
        }
      }
      // Handle Word files
      else if (contentType.includes('word') || contentType.includes('document') ||
               doc.filename.endsWith('.docx') || doc.filename.endsWith('.doc')) {
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
          setWordHtml(result.value);
        } catch (e) {
          console.error("Error parsing Word:", e);
        }
      }
      // Handle Image files - just display directly
      else if (contentType.startsWith('image/') || 
               /\.(png|jpg|jpeg|gif|webp|heic|tiff|tif)$/i.test(doc.filename)) {
        // Images are handled via blob URL directly
      }
      
      // Always create blob for download
      const blob = new Blob([arrayBuffer], { type: contentType });
      const url = URL.createObjectURL(blob);
      setDocumentBlob(url);
      
    } catch (error) {
      console.error("Error loading document:", error);
      alert("Erreur lors du chargement du document. Veuillez réessayer.");
      setViewerOpen(false);
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleDownloadDocument = () => {
    if (documentBlob && viewingDocument) {
      const a = document.createElement('a');
      a.href = documentBlob;
      a.download = viewingDocument.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Download all documents as ZIP
  const handleDownloadAllDocuments = async () => {
    if (documents.length === 0) {
      alert('Aucun document à télécharger');
      return;
    }
    
    setDownloadingAll(true);
    const zip = new JSZip();
    const headers = { Authorization: `Bearer ${token}` };
    let successCount = 0;
    let errorCount = 0;
    
    // Create folder structure in ZIP
    const folderMap = {};
    folders.forEach(folder => {
      folderMap[folder.id] = folder.name;
    });
    
    for (const doc of documents) {
      try {
        const response = await axios.get(
          `${API}/contracts/${contractId}/documents/${doc.id}`,
          { headers, responseType: 'arraybuffer' }
        );
        
        // Determine path in ZIP (folder/filename or just filename)
        let filePath = doc.filename;
        if (doc.folder_id && folderMap[doc.folder_id]) {
          filePath = `${folderMap[doc.folder_id]}/${doc.filename}`;
        } else {
          filePath = `Racine/${doc.filename}`;
        }
        
        zip.file(filePath, response.data);
        successCount++;
      } catch (error) {
        console.error(`Error downloading ${doc.filename}:`, error);
        errorCount++;
      }
    }
    
    if (successCount === 0) {
      alert('Aucun document n\'a pu être téléchargé. Les fichiers sont peut-être manquants.');
      setDownloadingAll(false);
      return;
    }
    
    try {
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      const safeSociete = (contract?.societe || 'contrat').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `documents_${safeSociete}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      if (errorCount > 0) {
        alert(`${successCount} document(s) téléchargé(s). ${errorCount} document(s) manquant(s).`);
      }
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Erreur lors de la création du ZIP');
    } finally {
      setDownloadingAll(false);
    }
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewingDocument(null);
    setExcelData(null);
    setWordHtml(null);
    if (documentBlob) {
      URL.revokeObjectURL(documentBlob);
      setDocumentBlob(null);
    }
  };

  const handleDeleteDocument = async (docId, filename) => {
    if (!window.confirm(`Supprimer le document "${filename}" ?`)) return;
    
    setDeleting(docId);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/contracts/${contractId}/documents/${docId}`, { headers });
      setDocuments(documents.filter(d => d.id !== docId));
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("Erreur lors de la suppression");
    } finally {
      setDeleting(null);
    }
  };

  // Multi-selection functions
  const toggleDocumentSelection = (docId, e) => {
    e.stopPropagation();
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const selectAllDocuments = () => {
    if (selectedDocuments.size === documents.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(documents.map(d => d.id)));
    }
  };

  const clearSelection = () => {
    setSelectedDocuments(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.size === 0) return;
    
    const count = selectedDocuments.size;
    if (!window.confirm(`Supprimer ${count} document${count > 1 ? 's' : ''} ?`)) return;
    
    setBulkOperationLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      for (const docId of selectedDocuments) {
        await axios.delete(`${API}/contracts/${contractId}/documents/${docId}`, { headers });
      }
      
      setDocuments(documents.filter(d => !selectedDocuments.has(d.id)));
      setSelectedDocuments(new Set());
    } catch (error) {
      console.error("Error bulk deleting:", error);
      alert("Erreur lors de la suppression");
      await fetchContractData();
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkMove = async (targetFolderId) => {
    if (selectedDocuments.size === 0) return;
    
    setBulkOperationLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      for (const docId of selectedDocuments) {
        let url = `${API}/contracts/${contractId}/documents/${docId}/move`;
        if (targetFolderId) {
          url += `?folder_id=${targetFolderId}`;
        }
        await axios.put(url, {}, { headers });
      }
      
      await fetchContractData();
      setSelectedDocuments(new Set());
      setBulkMoveDialogOpen(false);
    } catch (error) {
      console.error("Error bulk moving:", error);
      alert("Erreur lors du déplacement");
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  // Group documents by folder
  const rootDocuments = documents.filter(d => !d.folder_id);
  const getDocumentsByFolder = (folderId) => documents.filter(d => d.folder_id === folderId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-orange-500 mb-4" />
        <p className="text-muted-foreground">Contrat non trouvé</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      </div>
    );
  }

  const DocumentItem = ({ doc, inFolder = false }) => {
    const isSelected = selectedDocuments.has(doc.id);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${inFolder ? 'ml-6' : ''} ${isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50'}`}
        onClick={() => handleViewDocument(doc)}
      >
        <div className="flex items-center gap-3">
          {/* Checkbox */}
          <div 
            className="flex items-center justify-center"
            onClick={(e) => toggleDocumentSelection(doc.id, e)}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {}}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
          </div>
          {getFileIcon(doc.content_type)}
          <div>
            <p className="font-medium text-sm">{doc.filename}</p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(doc.size)} • {formatDateFR(doc.uploaded_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => handleViewDocument(doc)} title="Visualiser">
            <Eye className="w-4 h-4 text-blue-600" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { setDocumentToMove(doc); setMoveDialogOpen(true); }}>
                <MoveRight className="w-4 h-4 mr-2" /> Déplacer
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="contrat-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg sm:text-2xl font-bold text-foreground truncate">{contract.societe}</h1>
              {getStatusBadge(contract.status)}
              {contract.reconduction_state === "en_reconduction" && (
                <Badge className="bg-purple-100 text-purple-800 border-purple-300">🔁 En reconduction</Badge>
              )}
              {contract.reconduction_state === "reconduction_a_confirmer" && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300">⚠️ Reconduction à confirmer</Badge>
              )}
              {contract.tacite_reconduction && !contract.reconduction_state && (
                <Badge className="bg-blue-50 text-blue-700 border-blue-200" title="Tacite reconduction activée — surveillé">🔁 Tacite</Badge>
              )}
            </div>
            <p className="text-muted-foreground text-xs sm:text-sm">{contract.type} • {contract.produit}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ResiliationClientUploadContracts 
            contract={contract} 
            onSuccess={fetchContractData} 
            token={token}
          />
          <Button 
            onClick={() => setEditModalOpen(true)}
            className="gap-2"
            data-testid="edit-contract-btn"
          >
            <Pencil className="w-4 h-4" />
            <span className="hidden sm:inline">Modifier le contrat</span>
            <span className="sm:hidden">Modifier</span>
          </Button>
        </div>
      </div>

      {/* Carte Résiliation si en cours */}
      <ResiliationClientCard contract={contract} onRefresh={fetchContractData} token={token} />

      {/* Contract Info Grid - Champs en évidence (drag & drop + personnalisable) */}
      <ContractHighlightFields contract={contract} />

      {/* Section tacite reconduction — visible si contract flaggé OU en état spécifique */}
      {(contract.tacite_reconduction || contract.reconduction_state || (contract.historique_reconductions || []).length > 0) && (
        <ClientContractRenewalSection contract={contract} onUpdate={fetchContractData} />
      )}
      {!contract.tacite_reconduction && !contract.reconduction_state && !((contract.historique_reconductions || []).length > 0) && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between">
          <div className="text-sm text-slate-600 flex items-center gap-2">
            <RotateCw className="w-4 h-4 text-slate-400" />
            Ce contrat n'a pas de tacite reconduction configurée.
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowInlineTacit(true)} data-testid="enable-tacit-quick">
            Activer la tacite reconduction
          </Button>
        </div>
      )}
      {showInlineTacit && (
        <ClientContractRenewalSection contract={contract} onUpdate={() => { fetchContractData(); setShowInlineTacit(false); }} />
      )}

      {/* Contract Details - Section 1 */}
      <Card className="bg-white border" data-testid="contract-details-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Détails du contrat</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Informations générales */}
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">Informations générales</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-6">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium">{contract.type || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Produit</span>
              <span className="font-medium">{contract.produit || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Durée</span>
              <span className="font-medium">{contract.duree_mois ? `${contract.duree_mois} mois` : '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Date d'installation</span>
              <span className="font-medium">{formatDateFR(contract.date_installation)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Vendeur</span>
              <span className="font-medium">{contract.vendeur || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Revendeur</span>
              <span className="font-medium">{contract.revendeur || '-'}</span>
            </div>
          </div>

          {/* Montants et abonnements */}
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">Montants et abonnements</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-6">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Abonnement Gestion Interne</span>
              <span className="font-medium font-mono">{formatCurrency(contract.abonnement_gestion_interne)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Abonnement Finance</span>
              <span className="font-medium font-mono">{formatCurrency(contract.abonnement_finance)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Montant Total</span>
              <span className="font-medium font-mono text-primary">{formatCurrency(contract.montant_abonnement)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Frais d'installation</span>
              <span className="font-medium font-mono">{formatCurrency(contract.frais_installation)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Coût d'installation</span>
              <span className="font-medium font-mono">{formatCurrency(contract.cout_installation)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Location serveur</span>
              <span className="font-medium font-mono">{formatCurrency(contract.location_serveur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Vente matériel</span>
              <span className="font-medium font-mono">{formatCurrency(contract.vente_materiel)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Commission</span>
              <span className="font-medium font-mono">{formatCurrency(contract.commission)}</span>
            </div>
          </div>

          {/* Financement / Leasing */}
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">Financement / Leasing</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-6">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Leaser</span>
              <span className="font-medium">{contract.leaser || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">N° Financement</span>
              <span className="font-medium font-mono">{contract.numero_financement || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">CA Leaser</span>
              <span className="font-medium font-mono">{formatCurrency(contract.ca_leaser)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Rachat</span>
              <span className="font-medium font-mono">{formatCurrency(contract.rachat)}</span>
            </div>
          </div>

          {/* Informations bancaires */}
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">Informations bancaires</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Banque</span>
              <span className="font-medium">{contract.banque || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b md:col-span-2">
              <span className="text-muted-foreground">IBAN</span>
              <span className="font-medium font-mono text-xs">{contract.iban || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">BIC</span>
              <span className="font-medium font-mono">{contract.bic || '-'}</span>
            </div>
          </div>

          {/* Champs personnalisés */}
          {contract.custom_fields && Object.keys(contract.custom_fields).length > 0 && (
            <>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 mt-6 font-semibold">Champs personnalisés</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                {Object.entries(contract.custom_fields).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">{key}</span>
                    <span className="font-medium">{value || '-'}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          
          {/* Section Résiliation */}
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 mt-6 font-semibold">Paramètres de résiliation</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-muted-foreground">Durée de préavis</span>
              {editingPreavis ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="24"
                    value={preavisValue}
                    onChange={(e) => setPreavisValue(parseInt(e.target.value) || 0)}
                    className="w-20 h-8 text-sm"
                    data-testid="preavis-input"
                  />
                  <span className="text-sm">mois</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-7 px-2 text-green-600 hover:text-green-700"
                    onClick={handleSavePreavis}
                    disabled={savingPreavis}
                    data-testid="save-preavis-btn"
                  >
                    {savingPreavis ? <Loader2 className="w-4 h-4 animate-spin" /> : "OK"}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-7 px-2 text-gray-500"
                    onClick={() => {
                      setEditingPreavis(false);
                      setPreavisValue(contract.duree_preavis_resiliation_mois || 3);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{contract.duree_preavis_resiliation_mois || 3} mois</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-7 w-7 p-0"
                    onClick={() => setEditingPreavis(true)}
                    title="Modifier la durée de préavis"
                    data-testid="edit-preavis-btn"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Section with Folders - Section 2 */}
      <Card className="bg-white border" data-testid="documents-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Documents du contrat
              <Badge variant="secondary">{documents.length}</Badge>
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setNewFolderDialogOpen(true)}
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Nouveau dossier</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDownloadAllDocuments}
                disabled={downloadingAll || documents.length === 0}
                title="Télécharger tous les documents en ZIP"
              >
                {downloadingAll ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Archive className="w-4 h-4 mr-2" />
                )}
                <span className="hidden sm:inline">{downloadingAll ? 'Téléchargement...' : 'Télécharger tout'}</span>
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleFileUpload(e, selectedFolderId)}
                accept=".pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.heic,.tiff,.tif"
                multiple
                className="hidden"
              />
              <Button 
                onClick={() => { setSelectedFolderId(null); fileInputRef.current?.click(); }}
                disabled={uploading}
                size="sm"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploading ? "Upload..." : "Ajouter"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Selection toolbar */}
          {selectedDocuments.size > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedDocuments.size === documents.length}
                  onChange={selectAllDocuments}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm font-medium text-blue-800">
                  {selectedDocuments.size} document{selectedDocuments.size > 1 ? 's' : ''} sélectionné{selectedDocuments.size > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkMoveDialogOpen(true)}
                  disabled={bulkOperationLoading}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <MoveRight className="w-4 h-4 mr-2" />
                  Déplacer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkOperationLoading}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  {bulkOperationLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Supprimer
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            {/* Folders */}
            {folders.map((folder) => {
              const folderDocs = getDocumentsByFolder(folder.id);
              const isExpanded = expandedFolders[folder.id];
              
              return (
                <div key={folder.id} className="border rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                    onClick={() => toggleFolder(folder.id)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      {isExpanded ? (
                        <FolderOpen className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <Folder className="w-5 h-5 text-yellow-500" />
                      )}
                      <span className="font-medium">{folder.name}</span>
                      <Badge variant="outline" className="text-xs">{folderDocs.length}</Badge>
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedFolderId(folder.id);
                          fileInputRef.current?.click();
                        }}
                        title="Ajouter un fichier"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { 
                            setRenameFolderDialog(folder); 
                            setRenameFolderName(folder.name); 
                          }}>
                            <Pencil className="w-4 h-4 mr-2" /> Renommer
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteFolder(folder.id, folder.name)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t"
                      >
                        <div className="p-2 space-y-2">
                          {folderDocs.length > 0 ? (
                            folderDocs.map((doc) => (
                              <DocumentItem key={doc.id} doc={doc} inFolder />
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              Dossier vide
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}

            {/* Root Documents */}
            {rootDocuments.length > 0 && (
              <div className="space-y-2 pt-2">
                {folders.length > 0 && (
                  <p className="text-xs text-muted-foreground uppercase tracking-wider px-1">
                    Documents (hors dossier)
                  </p>
                )}
                {rootDocuments.map((doc) => (
                  <DocumentItem key={doc.id} doc={doc} />
                ))}
              </div>
            )}

            {/* Empty State */}
            {documents.length === 0 && folders.length === 0 && (
              <div className="py-12 text-center border-2 border-dashed rounded-lg">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-muted-foreground mb-2">Aucun document</p>
                <p className="text-xs text-muted-foreground mb-4">
                  Créez des dossiers pour organiser vos documents
                </p>
                <div className="flex justify-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setNewFolderDialogOpen(true)}
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Créer un dossier
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter un document
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Related Invoices Section - Section 3 */}
      <Card className="bg-white border" data-testid="invoices-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Factures liées au client
              <Badge variant="secondary">{relatedInvoices.length}</Badge>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loadingInvoices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : relatedInvoices.length > 0 ? (
            <div className="space-y-2">
              {(showAllInvoices ? relatedInvoices : relatedInvoices.slice(0, 5)).map((inv) => (
                <div 
                  key={inv.id || inv.numero}
                  className="p-3 bg-blue-50 rounded-lg flex justify-between items-center cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => {
                    setSelectedInvoice(inv);
                    if (inv.has_pdf) {
                      setPdfModalOpen(true);
                    }
                  }}
                  data-testid={`invoice-item-${inv.numero}`}
                >
                  <div className="flex items-center gap-3">
                    {inv.has_pdf && <Paperclip className="w-4 h-4 text-blue-500" />}
                    <div>
                      <p className="font-medium font-mono text-blue-700 hover:underline">{inv.numero}</p>
                      <p className="text-xs text-muted-foreground">{inv.type} • {formatDateFR(inv.date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold">{formatCurrency(inv.total_ht)}</p>
                    <p className="text-xs text-muted-foreground">TTC: {formatCurrency(inv.total_ttc)}</p>
                  </div>
                </div>
              ))}
              {relatedInvoices.length > 5 && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAllInvoices(!showAllInvoices)}
                    data-testid="toggle-invoices-btn"
                    className="gap-2"
                  >
                    {showAllInvoices ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Voir moins
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Voir plus ({relatedInvoices.length - 5} autres)
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm py-4 text-center">
              Aucune facture trouvée pour ce client
            </p>
          )}
        </CardContent>
      </Card>

      {/* New Folder Dialog */}
      <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau dossier</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Nom du dossier (ex: Contrat initial, Avenants...)"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}>
              {creatingFolder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={!!renameFolderDialog} onOpenChange={() => setRenameFolderDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer le dossier</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Nouveau nom"
              value={renameFolderName}
              onChange={(e) => setRenameFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFolderDialog(null)}>
              Annuler
            </Button>
            <Button onClick={handleRenameFolder} disabled={!renameFolderName.trim()}>
              Renommer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Document Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Déplacer "{documentToMove?.filename}"</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleMoveDocument(null)}
            >
              <FileText className="w-4 h-4 mr-2" />
              Racine (hors dossier)
            </Button>
            {folders.map((folder) => (
              <Button
                key={folder.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleMoveDocument(folder.id)}
                disabled={documentToMove?.folder_id === folder.id}
              >
                <Folder className="w-4 h-4 mr-2 text-yellow-500" />
                {folder.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={closeViewer}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {viewingDocument && getFileIcon(viewingDocument.content_type)}
                <div>
                  <DialogTitle className="text-base">{viewingDocument?.filename}</DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    {viewingDocument && formatFileSize(viewingDocument.size)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleDownloadDocument} disabled={!documentBlob}>
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden bg-gray-100">
            {loadingDocument ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Chargement du document...</span>
              </div>
            ) : documentBlob ? (
              // PDF Preview
              viewingDocument?.content_type?.includes('pdf') ? (
                <iframe
                  src={documentBlob}
                  className="w-full h-full border-0"
                  title={viewingDocument?.filename}
                />
              ) : // Image Preview
              viewingDocument?.content_type?.includes('image') ? (
                <div className="flex items-center justify-center h-full p-4">
                  <img 
                    src={documentBlob} 
                    alt={viewingDocument?.filename}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : // Excel Preview
              excelData ? (
                <div className="h-full overflow-auto p-4">
                  <div className="bg-white rounded-lg shadow">
                    {excelData.sheetNames.length > 1 && (
                      <div className="flex gap-2 p-2 border-b bg-gray-50">
                        {excelData.sheetNames.map((name) => (
                          <Button
                            key={name}
                            variant={excelData.currentSheet === name ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              const ws = excelData.workbook.Sheets[name];
                              const data = XLSX.utils.sheet_to_json(ws, { 
                                header: 1,
                                raw: false,
                                dateNF: 'dd/mm/yyyy'
                              });
                              setExcelData(prev => ({ ...prev, currentSheet: name, data }));
                            }}
                          >
                            {name}
                          </Button>
                        ))}
                      </div>
                    )}
                    <div className="overflow-auto max-h-[calc(90vh-180px)]">
                      <table className="w-full border-collapse text-sm">
                        <tbody>
                          {excelData.data.map((row, rowIndex) => (
                            <tr key={rowIndex} className={rowIndex === 0 ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"}>
                              {row.map((cell, cellIndex) => (
                                <td 
                                  key={cellIndex} 
                                  className="border border-gray-200 px-3 py-2 whitespace-nowrap"
                                >
                                  {cell !== null && cell !== undefined ? String(cell) : ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : // Word Preview
              wordHtml ? (
                <div className="h-full overflow-auto p-4">
                  <div 
                    className="bg-white rounded-lg shadow p-8 max-w-4xl mx-auto prose prose-sm"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(wordHtml) }}
                  />
                </div>
              ) : (
                // Unsupported format
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  {getFileIcon(viewingDocument?.content_type)}
                  <p className="mt-4 text-lg font-medium">{viewingDocument?.filename}</p>
                  <p className="mt-2 text-muted-foreground">
                    Ce type de fichier ne peut pas être prévisualisé dans le navigateur.
                  </p>
                  <Button className="mt-4" onClick={handleDownloadDocument}>
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger le fichier
                  </Button>
                </div>
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Impossible de charger le document</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Move Dialog */}
      <Dialog open={bulkMoveDialogOpen} onOpenChange={setBulkMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Déplacer {selectedDocuments.size} document{selectedDocuments.size > 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleBulkMove(null)}
              disabled={bulkOperationLoading}
            >
              {bulkOperationLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Racine (hors dossier)
            </Button>
            {folders.map((folder) => (
              <Button
                key={folder.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleBulkMove(folder.id)}
                disabled={bulkOperationLoading}
              >
                {bulkOperationLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Folder className="w-4 h-4 mr-2 text-yellow-500" />
                )}
                {folder.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Modal */}
      <ContractFormModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        contract={contract}
        onSuccess={() => {
          setEditModalOpen(false);
          fetchContractData();
        }}
      />

      {/* PDF Viewer Modal for Invoices */}
      <PDFViewerModal
        open={pdfModalOpen}
        onOpenChange={setPdfModalOpen}
        invoiceNumero={selectedInvoice?.numero}
        invoiceClient={selectedInvoice?.client}
      />
    </div>
  );
}
